import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAdminUsers } from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "spend_desc", label: "Spend (highest)" },
  { value: "spend_asc", label: "Spend (lowest)" },
  { value: "created_desc", label: "Newest first" },
  { value: "created_asc", label: "Oldest first" },
];

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("spend_desc");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const usersQ = useQuery({
    queryKey: ["admin-users", q, sort, offset],
    queryFn: () => getAdminUsers({ q: q || undefined, sort, limit, offset }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <SectionLabel>Search</SectionLabel>
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder="email contains…"
              className="mt-1 w-full rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[14px] outline-none focus:border-amber"
            />
          </div>
          <div>
            <SectionLabel>Sort</SectionLabel>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-cream text-left text-[11px] uppercase tracking-wider text-taupe">
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Lang</th>
              <th className="px-4 py-2">Joined</th>
              <th className="px-4 py-2 text-right">Spend · month</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2 text-right">Spend · all-time</th>
            </tr>
          </thead>
          <tbody>
            {usersQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-taupe">
                  Loading…
                </td>
              </tr>
            ) : (usersQ.data?.users || []).length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-taupe">
                  No users match.
                </td>
              </tr>
            ) : (
              usersQ.data!.users.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-cream-border hover:bg-cream/60"
                >
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/users/${u.id}`}
                      className="font-medium text-navy-deep hover:text-amber-dark"
                    >
                      {u.email}
                      {u.is_admin ? (
                        <span className="ml-2 rounded-full bg-amber/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-dark">
                          admin
                        </span>
                      ) : null}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-taupe">{u.language}</td>
                  <td className="px-4 py-2 text-taupe">
                    {u.created_at
                      ? new Date(u.created_at).toISOString().slice(0, 10)
                      : "—"}
                  </td>
                  <td className="metric-number px-4 py-2 text-right">
                    ${u.spend_this_month_usd.toFixed(4)}
                  </td>
                  <td className="metric-number px-4 py-2 text-right text-taupe">
                    {u.calls_this_month}
                  </td>
                  <td className="metric-number px-4 py-2 text-right text-taupe">
                    ${u.spend_total_usd.toFixed(4)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {usersQ.data && usersQ.data.total > limit ? (
        <div className="flex items-center justify-between text-[12px] text-taupe">
          <span>
            {offset + 1}–{Math.min(offset + limit, usersQ.data.total)} of{" "}
            {usersQ.data.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="rounded-md border border-cream-border px-3 py-1 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={offset + limit >= usersQ.data.total}
              onClick={() => setOffset(offset + limit)}
              className="rounded-md border border-cream-border px-3 py-1 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
